/**
 * Hook for chart mouse interaction handling.
 * Encapsulates hover, pan, and click logic.
 */

import { useCallback, useRef } from "react";

import {
  buildInteractionHoverEvent,
  eventToChartCoords,
  findNearestPointsForSeries,
} from "~utils/ticker-chart";
import type {
  ChartStore,
  ClickEvent,
  HoverEvent,
  Viewport,
} from "~types/types";

export interface UseChartInteractionOptions {
  store: ChartStore;
  seriesIds: string[];
  hoverEnabled: boolean;
  panEnabled: boolean;
  onHover?: (event: HoverEvent) => void;
  onClick?: (event: ClickEvent) => void;
}

export interface ChartInteractionHandlers {
  handleMouseMove: (event: React.MouseEvent<SVGRectElement>) => void;
  handleMouseLeave: () => void;
  handleMouseDown: (event: React.MouseEvent<SVGRectElement>) => void;
  handleMouseUp: () => void;
  handleClick: (event: React.MouseEvent<SVGRectElement>) => void;
}

/**
 * Hook that provides mouse event handlers for chart interaction.
 * Handles hover state, panning, and click events.
 */
/**
 * State captured at drag start for accurate pan calculation.
 * Storing initial viewport prevents accumulated rounding errors during drag.
 */
interface PanStartState {
  /** Initial chart X position at drag start */
  x: number;
  /** Timestamp at drag start position */
  timestamp: number;
  /** Viewport state at drag start - used for absolute delta calculation */
  initialViewport: Viewport;
}

export function useChartInteraction({
  store,
  seriesIds,
  hoverEnabled,
  panEnabled,
  onHover,
  onClick,
}: UseChartInteractionOptions): ChartInteractionHandlers {
  const storeRef = useRef(store);
  const panStartRef = useRef<PanStartState | null>(null);

  // Keep ref in sync with current store
  storeRef.current = store;

  const handleMouseMove = useCallback(
    (event: React.MouseEvent<SVGRectElement>) => {
      const currentStore = storeRef.current;
      const state = currentStore.getState();

      // Handle dragging (pan) during mouse move
      if (state.isDragging && panEnabled && panStartRef.current) {
        const rect = event.currentTarget.getBoundingClientRect();
        const layout = currentStore.getLayout();
        const currentChartX = event.clientX - rect.left - layout.priceArea.x;

        // Calculate delta from last position
        const deltaX = panStartRef.current.x - currentChartX;

        // Update pan start position for next move
        panStartRef.current.x = currentChartX;

        // Use panWithoutSnap for smooth dragging
        currentStore.panWithoutSnap(deltaX);

        return;
      }

      // Handle hover
      if (!hoverEnabled) return;

      const rect = event.currentTarget.getBoundingClientRect();
      const layout = currentStore.getLayout();
      const scales = currentStore.getScales();

      const { chartX, chartY, timestamp } = eventToChartCoords(
        event.clientX,
        event.clientY,
        rect.left,
        rect.top,
        layout,
        scales.x
      );

      const nearestPoints = findNearestPointsForSeries(
        seriesIds,
        chartX,
        (id) => currentStore.getVisibleData(id),
        scales.x
      );

      currentStore.setHover({
        visible: true,
        screenX: event.clientX,
        screenY: event.clientY,
        chartX,
        chartY,
        timestamp,
        nearestPoints,
      });

      if (onHover) {
        const hoverEvent = buildInteractionHoverEvent({
          mode: state.mode,
          screenX: event.clientX,
          screenY: event.clientY,
          chartX,
          chartY,
          timestamp,
          seriesIds,
          nearestPoints,
          seriesMetaMap: state.seriesMeta,
        });

        if (hoverEvent) {
          onHover(hoverEvent);
        }
      }
    },
    [seriesIds, hoverEnabled, panEnabled, onHover]
  );

  const handleMouseLeave = useCallback(() => {
    const currentStore = storeRef.current;

    // Clear pan state when mouse leaves
    if (panStartRef.current) {
      panStartRef.current = null;
      currentStore.setDragging(false);
    }

    if (hoverEnabled) {
      currentStore.clearHover();
    }
  }, [hoverEnabled]);

  const handleMouseDown = useCallback(
    (event: React.MouseEvent<SVGRectElement>) => {
      if (!panEnabled) return;

      const currentStore = storeRef.current;
      const rect = event.currentTarget.getBoundingClientRect();
      const layout = currentStore.getLayout();
      const chartX = event.clientX - rect.left - layout.priceArea.x;
      const timestamp = currentStore.getScales().x.invert(chartX);

      // Capture initial viewport for absolute delta calculation
      const initialViewport = { ...currentStore.getViewport() };

      panStartRef.current = { x: chartX, timestamp, initialViewport };
      currentStore.setDragging(true);

      // Hide crosshair during pan interactions
      currentStore.clearHover();
    },
    [panEnabled]
  );

  const handleMouseUp = useCallback(() => {
    const currentStore = storeRef.current;

    // Snap viewport to data points at end of pan interaction
    if (panStartRef.current) {
      currentStore.snapViewport();
    }

    panStartRef.current = null;
    currentStore.setDragging(false);
  }, []);

  const handleClick = useCallback(
    (event: React.MouseEvent<SVGRectElement>) => {
      if (!onClick) return;

      const currentStore = storeRef.current;
      const rect = event.currentTarget.getBoundingClientRect();
      const layout = currentStore.getLayout();
      const scales = currentStore.getScales();
      const state = currentStore.getState();

      const { chartX, chartY, timestamp } = eventToChartCoords(
        event.clientX,
        event.clientY,
        rect.left,
        rect.top,
        layout,
        scales.x
      );

      const nearestPoints = findNearestPointsForSeries(
        seriesIds,
        chartX,
        (id) => currentStore.getVisibleData(id),
        scales.x
      );

      const clickEvent = buildInteractionHoverEvent({
        mode: state.mode,
        screenX: event.clientX,
        screenY: event.clientY,
        chartX,
        chartY,
        timestamp,
        seriesIds,
        nearestPoints,
        seriesMetaMap: state.seriesMeta,
      });

      if (clickEvent) {
        onClick(clickEvent as ClickEvent);
      }
    },
    [seriesIds, onClick]
  );

  return {
    handleMouseMove,
    handleMouseLeave,
    handleMouseDown,
    handleMouseUp,
    handleClick,
  };
}
